import apiService from "./api";
import { sanitizeQueryParams } from "../utility/helpers/apiHelper";
import type { TableSortValue } from "@src/shared/constants/sort-order.constants";
import type { PaginationAndOrderParams } from "@src/shared/types/pagination.types";

export interface GetPayoutsParams
  extends Partial<
    Omit<PaginationAndOrderParams, "page" | "pageSize" | "orderDirection">
  > {
  page?: number | string;
  pageSize?: number | string;
  search?: string;
  status?: string;
  businessId?: string | number;
  orderDirection?: TableSortValue;
}

export interface PayoutLogRecord {
  id?: number | string;
  payoutId?: number | string;
  businessId?: number | string;
  eventType?: string | null;
  amount?: number;
  platformFee?: number;
  netAmount?: number;
  currency?: string | null;
  status?: string | null;
  message?: string | null;
  createdAt?: string;
}

export interface PayoutRecord {
  id?: number | string;
  businessId?: number | string;
  businessName?: string | null;
  totalAmount?: number;
  platformFee?: number;
  totalPlatformFee?: number;
  netAmount?: number;
  currency?: string | null;
  status?: string | null;
  idempotencyKey?: string | null;
  scheduledAt?: string | null;
  processedAt?: string | null;
  failureReason?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  createdBy?: number | null;
  logs?: PayoutLogRecord[];
}

export interface DisbursementLogRecord {
  id?: number | string;
  payoutId?: number | string;
  businessId?: number | string;
  businessName?: string | null;
  eventType?: string | null;
  amount?: number;
  platformFee?: number;
  netAmount?: number;
  currency?: string | null;
  status?: string | null;
  message?: string | null;
  createdAt?: string;
}

export class PayoutService {
  async getPayouts(query: GetPayoutsParams) {
    return apiService.get("/payouts", {
      params: sanitizeQueryParams(query),
    });
  }

  async getPayout(id: string) {
    return apiService.get(`/payouts/${id}`);
  }

  async processPayout(id: string, data?: { notes?: string }) {
    return apiService.post(`/payouts/${id}/process`, data || {});
  }

  async triggerBatchPayout(ids: string[]) {
    return apiService.post("/payouts/batch-process", { payoutIds: ids });
  }

  async resolvePayoutError(id: string, data: { action: string; note?: string }) {
    return apiService.post(`/payouts/${id}/resolve-error`, data);
  }

  async getDisbursementLogs(query?: GetPayoutsParams) {
    return apiService.get("/disbursement-logs", {
      params: sanitizeQueryParams(query || {}),
    });
  }
}

export const payoutService = new PayoutService();
